import { SongViewWithOrg } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from './client_local_storage.ts';

// Song views are queued in local storage and uploaded in the background. The queue is cumulative
// (a view stays queued until the server has acknowledged it), so uploads must run one at a time
// (issue #3709): two uploads in flight at once both carry the queue, and the server records every
// view in it twice. A device whose requests stalled while it kept playing - 54 stalled requests on
// one afternoon - then flushed them all together recorded each play once per stalled request.
class SongViews {
  private uploadInFlight: Promise<void> | undefined;
  private uploadRequested = false;

  add(songView: SongViewWithOrg) {
    const cachedSongViews = localStorageGet(LocalStorageKey.SongViews);
    cachedSongViews.push(songView);
    localStorageSet(LocalStorageKey.SongViews, cachedSongViews);
  }

  // Never rejects: callers fire and forget (the player records views on a poll and on unmount), so
  // a failed upload (offline, or no server at all as in Storybook) is logged and the cached song
  // views stay put for the next upload, rather than surfacing as an unhandled rejection.
  //
  // A call while an upload is in flight does not start another: it asks for one more upload once
  // the current one has finished, which then carries whatever has been queued in the meantime.
  async uploadToServer(): Promise<void> {
    if (this.uploadInFlight) {
      this.uploadRequested = true;
      await this.uploadInFlight;
      return;
    }
    this.uploadInFlight = this.uploadQueuedSongViews().finally(() => {
      this.uploadInFlight = undefined;
      if (this.uploadRequested) {
        this.uploadRequested = false;
        void this.uploadToServer();
      }
    });
    await this.uploadInFlight;
  }

  private async uploadQueuedSongViews(): Promise<void> {
    const household = localStorageGet(LocalStorageKey.Household);
    const songViewsToUpload = localStorageGet(LocalStorageKey.SongViews);
    if (songViewsToUpload.length === 0) {
      return;
    }
    try {
      await server_api.reportSongViews(household?.token, songViewsToUpload);
    } catch (error: any) {
      console.error(`Failed to upload song views, will retry: ${error.message}`);
      return;
    }
    // Remove only what was uploaded: `add` appends, so a view recorded while the request was in
    // flight sits after the uploaded ones and stays queued for the next upload.
    const remainingSongViews = localStorageGet(LocalStorageKey.SongViews).slice(songViewsToUpload.length);
    if (remainingSongViews.length === 0) {
      localStorageRemove(LocalStorageKey.SongViews);
    } else {
      localStorageSet(LocalStorageKey.SongViews, remainingSongViews);
    }
  }
}

export const songViews = new SongViews();
